import { defineStore } from "pinia";
import { useQuoteStore } from "./quotes";
import { useAboutYouStore } from "./aboutYou";
import { useApplicationStore } from "./application";
import { useDailyActivitiesStore } from "./dailyActivities";
import { useDependantAboutYouStore } from "./dependantAboutYou";
import { useFinallyStore } from "./finally";
import { usePensionOptionsStore } from "./pensionOptions";
import { useYourDetailsStore } from "./yourDetails";
import { useDependantDailyActivitiesStore } from "./dependantDailyActivities";
import { z } from "zod";
import { jsonParse } from "@/utils";
import Cookies from "universal-cookie";
import { useConfigStore } from "./config";

export const LOGIN_KEY = "annuity_ready_login";
export const SESSION_EXPIRY = (new Date()).getTime() + (60 * 60 * 1000);

export type LoginSessionStorage = {
    codeVerifier: string,
    email: string,
    loginAttempts: number,
    expiry: number,
    isLoggedIn: boolean,
}

export const enum ResponseCodes {
    "BadRequest" = 400,
    "Error" = 500,
    "TooManyRequests" = 429,
    "FailedCode" = 403,
    "Ok" = 200,
    "Problem",
}

export const ResponseCodeMap = {
    [ResponseCodes.BadRequest]:
        "Something was wrong with your request. Please check your inputted data and try again.",
    [ResponseCodes.Error]: "Something went wrong processing your request.",
    [ResponseCodes.TooManyRequests]: "You've made too many requests. Please wait a minute and try again.",
    [ResponseCodes.Problem]: "We encountered a problem whilst processing your request.",
    [ResponseCodes.FailedCode]:
        "The code you entered was incorrect. Please try again or select the link below for a new code.",
};

type LoginStore = {
    email: string,
    code: string,
    isLoggedIn: boolean,
    loginAttempts: number,
    expiry: number,
    codeVerifier: string,
}

export const useLoginStore = defineStore("login", {
    state: () : LoginStore => {
        return {
            email: "",
            code: "",
            isLoggedIn: false,
            loginAttempts: 0,
            expiry: 0,
            codeVerifier: "",
        };
    },
    getters: {
        LOGIN_KEY: () => {
            return LOGIN_KEY;
        },
    },
    actions: {
        clearAllStores() {
            useQuoteStore().$reset();
            useAboutYouStore().$reset();
            useDependantAboutYouStore().$reset();
            useApplicationStore().$reset();
            useDailyActivitiesStore().$reset();
            useDependantDailyActivitiesStore().$reset();
            useFinallyStore().$reset();
            usePensionOptionsStore().$reset();
            useYourDetailsStore().$reset();
        },
        login() {
            this.isLoggedIn = true;
            sessionStorage.setItem(LOGIN_KEY, JSON.stringify(
                {
                    isLoggedIn: true,
                    codeVerifier: this.codeVerifier,
                    email: this.email,
                    loginAttempts: this.loginAttempts,
                    expiry: (new Date()).getTime() + (60 * 60 * 1000),
                    code: this.code,
                },
            ));
        },
        logout() {
            this.isLoggedIn = false;
            this.loginAttempts = 0;
            this.email = "";
            this.code = "";
            this.codeVerifier = "";
            this.expiry = 0;
            sessionStorage.clear();
            const cookies = new Cookies();
            const configStore = useConfigStore();
            cookies.remove(configStore.sessionCookieKey);
            this.clearAllStores();
        },
        updateLoginState() {
            if (!this.email) {
                const loginSession = sessionStorage.getItem(LOGIN_KEY);
                if (loginSession) {
                    const valid = z.object({
                        codeVerifier: z.string(),
                        email: z.string(),
                        loginAttempts: z.number(),
                        expiry: z.number(),
                        isLoggedIn: z.union([z.boolean(), z.number()])
                            .transform((val) => Boolean(val)),
                        code: z.string(),
                    }).parse(jsonParse(loginSession));
                    this.code = valid.code;
                    this.codeVerifier = valid.codeVerifier;
                    this.loginAttempts = valid.loginAttempts;
                    this.isLoggedIn = valid.isLoggedIn;
                    this.email = valid.email;
                    this.expiry = valid.expiry;
                }
            }
            sessionStorage.setItem(LOGIN_KEY, JSON.stringify(
                {
                    isLoggedIn: this.isLoggedIn,
                    codeVerifier: this.codeVerifier,
                    email: this.email,
                    loginAttempts: this.loginAttempts,
                    expiry: this.expiry,
                    code: this.code,
                },
            ));
        },
    },
});
